/**
 * Configuration constants for Provet booking system
 *
 * This file centralizes all magic numbers, timeouts, delays, and configuration values
 * used throughout the Provet online booking feature.
 *
 * @module provetConstants
 */

// =============================================================================
// API Configuration
// =============================================================================

/**
 * API timeout configurations (milliseconds)
 */
export const API_TIMEOUTS = {
  /** Standard API request timeout */
  REQUEST: 30000,

  /**
   * Booking submission timeout. Longer than the server's slowest path through Provet (about
   * 45 s: create the appointment, then the online-booking client and pet), so the customer is
   * never told a booking timed out while it is still being created.
   */
  BOOKING: 60000,

  /** Authentication timeout */
  AUTH: 20000,

  /** Default buffer time for appointments (minutes) */
  APPOINTMENT_BUFFER: 120
} as const;

// =============================================================================
// UI Delays & Timeouts
// =============================================================================

/**
 * UI delays and timeouts (milliseconds)
 */
export const UI_DELAYS = {
  /** Small delay to ensure DOM is updated */
  DOM_UPDATE: 50,

  /** Delay before scrolling to validation errors */
  VALIDATION_SCROLL: 200,

  /** Delay for EPI forms event triggers */
  FORM_EVENT_TRIGGER: 250,

  /** Delay before focusing after smooth scroll */
  FOCUS_AFTER_SCROLL: 300,

  /** Delay for field change triggers */
  FIELD_CHANGE_TRIGGER: 1250,

  /** Retry delay for element finding (base delay, multiplied by attempt number) */
  ELEMENT_FIND_RETRY: 50
} as const;

// =============================================================================
// Scroll Configuration
// =============================================================================

/**
 * Scroll behavior configuration
 */
export const SCROLL_CONFIG = {
  /** Default offset from top when scrolling to elements (pixels) */
  DEFAULT_OFFSET: 180,

  /** Offset for dialog scroll (pixels) */
  DIALOG_OFFSET: 140,

  /** Default scroll behavior */
  DEFAULT_BEHAVIOR: "smooth" as ScrollBehavior,

  /** Max attempts to find element before giving up */
  MAX_FIND_ATTEMPTS: 5
} as const;

// =============================================================================
// Loading States
// =============================================================================

/**
 * Loading spinner and state configuration
 */
export const LOADING_CONFIG = {
  /** Minimum height for department list spinner (pixels) */
  DEPARTMENT_MIN_HEIGHT: 300,

  /** Minimum height for treatment list spinner (pixels) */
  TREATMENT_MIN_HEIGHT: 200,

  /** Minimum height for generic inline loading (pixels) */
  DEFAULT_MIN_HEIGHT: 200,

  /** Spinner size (pixels) */
  SPINNER_SIZE: 32
} as const;

// =============================================================================
// Validation
// =============================================================================

/**
 * Form validation configuration and Provet API field requirements
 */
export const VALIDATION = {
  /** CSS selector for validation error elements */
  ERROR_SELECTOR: ".validation-error",

  // OnlineBookingClient field limits (from Provet API spec)
  /** Max length for client first name (Provet API requirement) */
  MAX_CLIENT_FIRST_NAME_LENGTH: 100,

  /** Max length for client last name (Provet API requirement) */
  MAX_CLIENT_LAST_NAME_LENGTH: 100,

  /** Max length for client email (Django EmailField limit) */
  MAX_CLIENT_EMAIL_LENGTH: 254,

  /** Max length for client phone number (Provet API requirement) */
  MAX_CLIENT_PHONE_LENGTH: 30,

  /** Max length for client street address (Provet API requirement) */
  MAX_CLIENT_STREET_ADDRESS_LENGTH: 100,

  /** Max length for client zip code (Provet API requirement) */
  MAX_CLIENT_ZIP_CODE_LENGTH: 12,

  /** Max length for client city (Provet API requirement) */
  MAX_CLIENT_CITY_LENGTH: 100,

  /** Max length for US state code (Provet API requirement) */
  MAX_CLIENT_STATE_LENGTH: 2,

  /** Max length for country region (Provet API requirement) */
  MAX_CLIENT_COUNTRY_REGION_LENGTH: 100,

  /** Max length for client ID number (Provet API requirement) */
  MAX_CLIENT_ID_NUMBER_LENGTH: 63,

  // OnlineBookingPatient field limits (from Provet API spec)
  /** Max length for patient name (Provet API requirement) */
  MAX_PATIENT_NAME_LENGTH: 100,

  /** Max length for patient notes (Provet API requirement) */
  MAX_PATIENT_NOTES_LENGTH: 1000,

  /** Max length for patient microchip (Provet API requirement) */
  MAX_PATIENT_MICROCHIP_LENGTH: 20
} as const;

// =============================================================================
// Business Rules
// =============================================================================

/**
 * Business rules and thresholds
 */
export const BUSINESS_RULES = {
  /** Hours before appointment when SMS reminder threshold applies */
  SMS_REMINDER_THRESHOLD_HOURS: 48,

  /** Minutes to add for immediate SMS send */
  SMS_IMMEDIATE_DELAY_MINUTES: 1
} as const;

// =============================================================================
// API Endpoints
// =============================================================================

/**
 * API endpoint paths (relative to /api/provet/ baseUrl in ApiClient)
 * These are used with ApiClient which prepends the baseUrl automatically.
 */
export const API_ENDPOINTS = {
  /** Fetch all departments */
  DEPARTMENTS: "/departments/",

  /** Search departments by query - template for building search URLs */
  DEPARTMENT_SEARCH: "/departments/search",

  /** Fetch reasons for a specific department by ID (use with template string) */
  REASONS_FOR_DEPARTMENT: (id: number) => `/reasons/${id}`,

  /** Fetch all reasons (treatments) */
  REASONS: "/reasons/",

  /** Fetch all reason groups */
  REASON_GROUPS: "/reason-groups/",

  /** Fetch all breeds */
  BREEDS: "/breeds/",

  /** Fetch all species */
  SPECIES: "/species/",

  /** Fetch available appointment times */
  APPOINTMENT_TIMES: "/appointment-times",

  /** Create booking */
  BOOKING: "/booking",

  /** Get care unit by PMS ID (use with query params) */
  CARE_UNIT: "/care-unit",

  /**
   * Cancel booking by appointment ID
   * @param id - Appointment ID
   */
  CANCEL_BOOKING: (id: number) => `/appointment/${id}/cancel_appointment/`,

  /** Create SMS verification code */
  CREATE_VERIFICATION: "/create-verification/",

  /** Check SMS verification code */
  CHECK_VERIFICATION: "/check-verification/",

  /** Check if user exists in Provet by email and phone */
  CHECK_USER_EXISTS: "/check-user-exists/"
} as const;

// =============================================================================
// Type Exports
// =============================================================================

/**
 * Type-safe access to API timeout keys
 */
export type ApiTimeoutKey = keyof typeof API_TIMEOUTS;

/**
 * Type-safe access to UI delay keys
 */
export type UIDelayKey = keyof typeof UI_DELAYS;

/**
 * Type-safe access to scroll config keys
 */
export type ScrollConfigKey = keyof typeof SCROLL_CONFIG;

/**
 * Type-safe access to loading config keys
 */
export type LoadingConfigKey = keyof typeof LOADING_CONFIG;

/**
 * Type-safe access to validation config keys
 */
export type ValidationKey = keyof typeof VALIDATION;

/**
 * Type-safe access to business rule keys
 */
export type BusinessRuleKey = keyof typeof BUSINESS_RULES;

/**
 * Type-safe access to API endpoint keys
 */
export type ApiEndpointKey = keyof typeof API_ENDPOINTS;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Converts minutes to milliseconds
 * @param minutes - Number of minutes
 * @returns Milliseconds
 */
export const minutesToMs = (minutes: number): number => minutes * 60 * 1000;

/**
 * Converts hours to milliseconds
 * @param hours - Number of hours
 * @returns Milliseconds
 */
export const hoursToMs = (hours: number): number => hours * 60 * 60 * 1000;

/**
 * Converts seconds to milliseconds
 * @param seconds - Number of seconds
 * @returns Milliseconds
 */
export const secondsToMs = (seconds: number): number => seconds * 1000;

// =============================================================================
// Exports
// =============================================================================

/**
 * All configuration constants grouped for convenient access
 */
export const ProvetConfig = {
  apiTimeouts: API_TIMEOUTS,
  uiDelays: UI_DELAYS,
  scrollConfig: SCROLL_CONFIG,
  loadingConfig: LOADING_CONFIG,
  validation: VALIDATION,
  businessRules: BUSINESS_RULES,
  apiEndpoints: API_ENDPOINTS,
  helpers: {
    minutesToMs,
    hoursToMs,
    secondsToMs
  }
} as const;

export default ProvetConfig;
